'use client';

import Link from 'next/link';
import { useState } from 'react';

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    className={`flex-none transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
  >
    <path d='m6 9 6 6 6-6' />
  </svg>
);

interface BenefitItem {
  icon: React.ReactNode;
  title: string;
  content: React.ReactNode;
}

export const ProductBenefitsAccordion = ({ isSignedIn }: { isSignedIn: boolean }) => {
  const [openIndexes, setOpenIndexes] = useState<number[]>([0]);

  const benefits: BenefitItem[] = [
    {
      icon: (
        <img
          src='/images/product-benefits-accordion/fsfm.svg'
          alt='Free shipping for members icon'
          title='Free Shipping For Members'
          width={24}
          height={24}
          className='h-6 w-6 flex-none object-contain lg_h-8 lg_w-8'
        />
      ),
      title: 'Free Shipping  For Members',
      content: (
        <p className='font-roboto-regular text-sm leading-relaxed text-333'>
          <Link href='/create-account' className='underline font-roboto-medium'>Sign up</Link>
          {' '}for a free account to unlock free standard shipping on every single order—no minimum spend required! Just log in before checkout, and your discount applies automatically. It&apos;s that easy.
        </p>
      ),
    },
    {
      icon: (
        <img
          src='/images/product-benefits-accordion/frw45d.svg'
          alt='Free returns truck icon'
          title='Free Returns within 45 Days'
          width={24}
          height={24}
          className='h-6 w-6 flex-none object-contain lg_h-8 lg_w-8'
        />
      ),
      title: 'Free Returns Within 45 Days',
      content: (
        <p className='font-roboto-regular text-sm leading-relaxed text-333'>
          We offer a 30-day return window for guests, which is extended to 45 days for HISEA members. Please note that if your return is due to a product issue, we will take full responsibility and ship a free replacement immediately—no return required.
        </p>
      ),
    },
    {
      icon: (
        <img
          src='/images/product-benefits-accordion/lwfaf.svg'
          alt='Lifetime warranty shield icon'
          title='Lifetime Warranty for All Footwear'
          width={24}
          height={24}
          className='h-6 w-6 flex-none object-contain lg_h-8 lg_w-8'
        />
      ),
      title: 'Lifetime Warranty For All Footwear',
      content: (
        <p className='font-roboto-regular text-sm leading-relaxed text-333'>
          Every pair of HISEA footwear is built to last. We proudly back our products with a lifetime warranty against defects in materials and workmanship. If your boots fail due to a manufacturing issue, we&apos;ll make it right with a repair or replacement.{' '}
          <Link href='/warranty' className='underline font-roboto-medium'>Learn more</Link>
        </p>
      ),
    },
    {
      icon: (
        <img
          src='/images/product-benefits-accordion/eroep.svg'
          alt='Earn rewards icon'
          title='Earn Rewards on Every Purchase'
          width={24}
          height={24}
          className='h-6 w-6 flex-none object-contain lg_h-8 lg_w-8'
        />
      ),
      title: 'Earn Rewards On Every Purchase',
      content: (
        <p className='font-roboto-regular text-sm leading-relaxed text-333'>
          Shop and get rewarded! Earn 1 reward point for every $1 spent—plus more for product reviews. Redeem your points for exclusive discounts on future orders. The more you explore, the more you save.{' '}
          <Link href='/advtr' className='underline font-roboto-medium'>Learn more rewards</Link>
        </p>
      ),
    },
  ];

  const toggle = (index: number) => {
    setOpenIndexes(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  return (
    <div className='mt-2 px-3 lg_mt-3 lg_px-0'>
      <div>
        {benefits.map((item, index) => (
          <div key={index}>
            <button
              type='button'
              onClick={() => toggle(index)}
              className='flex w-full items-center justify-between gap-x-3 py-4 text-left'
            >
              <div className='flex items-center gap-x-3'>
                {item.icon}
                <span className='font-roboto-medium text-base font-medium'>{item.title}</span>
              </div>
              <ChevronIcon open={openIndexes.includes(index)} />
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ${
                openIndexes.includes(index) ? 'max-h-[500px] pb-4' : 'max-h-0'
              }`}
            >
              {item.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
